import type {
  OverviewResponse,
  PipelineResponse,
  RepsResponse,
  SourcesResponse,
  TrendsResponse,
  CanvasingResponse,
  LeaderboardResponse,
  SalesReportParams,
  TrendsParams,
  ExportParams,
  LeaderboardParams,
} from '../types/salesReports'
import { authService } from './auth'

const BASE_URL = '/api/crm/sales-reports'

// Helper to build query strings
const buildQueryString = (params: Record<string, any>): string => {
  const query = new URLSearchParams()
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      query.append(key, String(value))
    }
  })
  return query.toString() ? `?${query.toString()}` : ''
}

// Generic API fetch function
async function apiFetch<T>(
  url: string,
  options: RequestInit = {}
): Promise<T> {
  const accessToken = authService.getAccessToken()

  const headers: HeadersInit = {
    'Content-Type': 'application/json',
    ...(accessToken && { Authorization: `Bearer ${accessToken}` }),
    ...options.headers,
  }

  const response = await fetch(url, {
    ...options,
    headers,
  })

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'An error occurred' }))
    throw error
  }

  if (response.status === 204 || response.headers.get('content-length') === '0') {
    return undefined as T
  }

  return response.json()
}

// Sales Reports API
export const salesReportsApi = {
  /**
   * Get overview metrics with period comparisons
   */
  overview: (params?: SalesReportParams) =>
    apiFetch<OverviewResponse>(`${BASE_URL}/overview/${buildQueryString(params || {})}`),

  /**
   * Get pipeline analytics including stage distribution, velocity, and forecasting
   */
  pipeline: (params?: SalesReportParams) =>
    apiFetch<PipelineResponse>(`${BASE_URL}/pipeline/${buildQueryString(params || {})}`),

  /**
   * Get sales rep performance metrics and leaderboards
   */
  reps: (params?: SalesReportParams) =>
    apiFetch<RepsResponse>(`${BASE_URL}/reps/${buildQueryString(params || {})}`),

  /**
   * Get lead source performance and ROI analysis
   */
  sources: (params?: SalesReportParams) =>
    apiFetch<SourcesResponse>(`${BASE_URL}/sources/${buildQueryString(params || {})}`),

  /**
   * Get historical trends and year-over-year comparisons
   */
  trends: (params?: TrendsParams) =>
    apiFetch<TrendsResponse>(`${BASE_URL}/trends/${buildQueryString(params || {})}`),

  /**
   * Get canvasing (door-to-door) activity metrics
   */
  canvasing: (params?: SalesReportParams) =>
    apiFetch<CanvasingResponse>(`${BASE_URL}/canvasing/${buildQueryString(params || {})}`),

  /**
   * Get team leaderboard with composite scores
   */
  leaderboard: (params?: LeaderboardParams) =>
    apiFetch<LeaderboardResponse>(`${BASE_URL}/leaderboard/${buildQueryString(params || {})}`),

  /**
   * Export report data as CSV
   */
  exportCsv: async (params: ExportParams): Promise<Blob> => {
    const accessToken = authService.getAccessToken()

    const response = await fetch(
      `${BASE_URL}/export-csv/${buildQueryString(params)}`,
      {
        headers: {
          ...(accessToken && { Authorization: `Bearer ${accessToken}` }),
        },
      }
    )

    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: 'Export failed' }))
      throw error
    }

    return response.blob()
  },

  /**
   * Download CSV export
   */
  downloadCsv: async (params: ExportParams): Promise<void> => {
    const blob = await salesReportsApi.exportCsv(params)
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = `sales_${params.report_type}_${params.date_from || 'all'}_${params.date_to || 'all'}.csv`
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    URL.revokeObjectURL(url)
  },
}

export default salesReportsApi
